import React, {useContext, useEffect} from "react";
import {createUseStyles} from "react-jss";
import {useNavigate} from "react-router-dom";
import {Theme} from "engage-client/theme";
import {EngageAppContext} from "../../App";
import {EngageMessageType} from "engage-common/messages";
import {ErrorType} from "engage-client/context";
import UserProvidedText from "engage-client/components/UserProvidedText";
import clsx from "clsx";


// @ts-ignore
const useStyles = createUseStyles<Theme>((theme) => ({
	background: {
		width: "100%",
		height: "100%",
		backgroundSize: "cover",
		backgroundRepeat: "no-repeat",
		backgroundImage: "url('assets/marketing/classic/bg-large.png')",
		[theme.breakpoints.down("sm")] : {
			backgroundImage: "url('assets/marketing/classic/bg-small.png')",
			backgroundSize: "100%",
			backgroundColor: "#071931",
		}
	},
	phone: {
		width: 626,
		height: 738,
		position: "absolute",
		bottom: 0,
		right: 50,
		backgroundSize: "cover",
		backgroundRepeat: "no-repeat",
		backgroundImage: "url('assets/marketing/classic/phone-large.png')",
		[theme.breakpoints.down("sm")] : {
			backgroundImage: "unset",
			right:0,
			height: "100%",
			width: "100%"
		},
	},
	welcomeText: {
		position: "absolute",
		top: 120,
		left: 60,
		right: 60,
		textAlign: "center",
		[theme.breakpoints.down("sm")] : {
			top: 30,
			left: 20,
			right: 20
		}
	},
	title: {
		position: "absolute",
		top: 240,
		left: 60,
		right: 60,
		height: 100,
		textAlign: "center",
		[theme.breakpoints.down("sm")] : {
			top: 130,
			left: 20,
			right: 20,
			height: 65
		}
	},
	subtitle: {
		position: "absolute",
		top: 335,
		left: 60,
		right: 60,
		height: 36,
		textAlign: "center",
		fontStyle: "oblique",
		[theme.breakpoints.down("sm")] : {
			top: 195,
			left: 20,
			right: 20
		}
	},
	timeText: {
		opacity: 0.9,
		height: 70,
		[theme.breakpoints.down("sm")] : {
			height: 40
		}
	},
	dateText: {
		opacity: 0.6,
		height: 60,
		[theme.breakpoints.down("sm")] : {
			height: 40
		},
		"&.middle": {
			marginBottom: ({compressed}) => compressed ? 10 : 20
		}
	},
	shadedArea: {
		display:"flex",
		flexDirection: "column",
		justifyContent: "center",
		backgroundColor: "#011f3741",
		position: "absolute",
		padding: ({compressed}) => compressed ? "10px 40px 10px 20px" : "20px 40px 20px 20px",
		left: 0,
		right: 20,
		top: 310,
		borderRadius: "0 138px 138px 0",
		maxHeight: 275,
		[theme.breakpoints.up("md")] : {
			left: 33,
			right: 60,
			top: 390,
		}
	}
}));

export type GameTime = {
	timeText: string
	dateText: string
}

type MarketingProps = {
	lobbyCode: string
	welcomeText: string
	title: string
	subtitle: string
	times: GameTime[]
}

export default function Marketing(props: MarketingProps) {
	const {sendMessage, lobbyValidation, clearError} = useContext(EngageAppContext.current);
	const navigate = useNavigate();
	const compressed = props.times.length >= 3;
	const classes = useStyles({compressed});

	useEffect(() => {
		//run right away then every 30 seconds to see if the lobby is around
		const timeout = setTimeout(() => {
			sendMessage({type:EngageMessageType.CheckLobby, lobbyID:props.lobbyCode});
		},500);
		const interval = setInterval(() => {
			sendMessage({type:EngageMessageType.CheckLobby, lobbyID:props.lobbyCode});
		}, 30000);
		return () => {clearInterval(interval);clearTimeout(timeout);};
	}, [sendMessage]);

	useEffect(() => {
		if(lobbyValidation) {
			clearError(ErrorType.NoLobby);
			navigate(`/j/${lobbyValidation.lobbyID}`);
		}
	}, [clearError, lobbyValidation, navigate]);

	return (
		<div className={classes.background}>
			<div className={classes.phone}>
				<UserProvidedText className={classes.welcomeText} maxFont={108} color="#07243F" fontFamily='"SignPainter"' fullHeight={false}>
					{props.welcomeText}
				</UserProvidedText>
				<UserProvidedText className={classes.title} maxFont={88} color="white" fullHeight={false} weight={900}>
					{props.title}
				</UserProvidedText>
				<UserProvidedText className={classes.subtitle} maxFont={30} color="white" fullHeight={false} weight={500}>
					{props.subtitle}
				</UserProvidedText>
				<div className={classes.shadedArea}>
					{props.times.map((t,i) =>
						<React.Fragment key={i}>
						<UserProvidedText className={classes.timeText} maxFont={compressed ? 35: 50} color="white" fullHeight={false} weight={500}>
							{t.timeText}
						</UserProvidedText>
						<UserProvidedText className={clsx(classes.dateText,i<props.times.length-1 && "middle")}
										  maxFont={compressed ? 30 : 40} color="white" fullHeight={false} weight={500}>
							{t.dateText}
						</UserProvidedText>
					</React.Fragment>)
					}

				</div>
			</div>
		</div>
	);
};